import React, { useState, useEffect } from "react";
import { Trash2, Edit2 } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { DatePicker } from "rsuite";
import { BsCalendar2MonthFill } from "react-icons/bs";
import BatchSelectModal from "../pharmacy/pharmacymodels/BatchSelectModal";
import { getIn } from "formik";

const SaleInvoiceProductTable = ({
  products,
  setProducts,
  newProduct,
  setNewProduct,
  onShowProductModal,
  overallDiscountPercent,
  setTotalAmount,
  formik
}) => {
  console.log("ProductTable Rendered", newProduct);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // ✅ Track which row is being edited

  const noArrowsCSS = `
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `;



  // ✅ Margin Calculation
  const calculateMargin = (
    mrp,
    rate,
    discPer,
    gstPer,
    numOfPacks,
    loose, // kept in signature for compatibility, not used
    overallDiscPercent = 0
  ) => {
    const MRP = parseFloat(mrp) || 0;
    const RATE = parseFloat(rate) || 0;
    const DISC = parseFloat(discPer) || 0;
    const GST = parseFloat(gstPer) || 0;
    const OVERALL = parseFloat(overallDiscPercent) || 0;
    const QTY = parseFloat(numOfPacks) || 0;

    // need valid inputs
    if (MRP <= 0 || RATE <= 0 || QTY <= 0) return 0;

    // scheme/loose ignored -> per-unit cost is simply RATE
    const baseRate = RATE;

    // Apply discounts (percentage)
    const totalDisc = DISC + OVERALL;
    const rateAfterDisc = baseRate * (1 - totalDisc / 100);

    // Apply GST
    const rateWithGst = rateAfterDisc * (1 + GST / 100);

    // Margin relative to MRP
    const margin = ((MRP - rateWithGst) / MRP) * 100;
    return Number.isFinite(margin) ? Number(margin.toFixed(2)) : 0;
  };

  // ✅ Amount Calculation
  const calculateAmount = (
    numOfPacks,
    loose, // kept in signature for compatibility, not used
    rate,
    discPer,
    gstPer,
    overallDiscPercent = 0
  ) => {
    const QTY = parseFloat(numOfPacks) || 0;
    const RATE = parseFloat(rate) || 0;
    const DISC = parseFloat(discPer) || 0;
    const GST = parseFloat(gstPer) || 0;
    const OVERALL = parseFloat(overallDiscPercent) || 0;

    if (QTY <= 0 || RATE <= 0) return 0;

    // scheme/loose ignored -> per-unit price is RATE
    const baseRate = RATE;

    // Apply discounts
    const totalDisc = DISC + OVERALL;
    const rateAfterDisc = baseRate * (1 - totalDisc / 100);

    // Apply GST
    const rateWithGst = rateAfterDisc * (1 + GST / 100);

    // Total payable is billed quantity * per-billed-unit price (including GST)
    const total = QTY * rateWithGst;
    return Number.isFinite(total) ? Number(total.toFixed(2)) : 0;
  };

  // helper to compute total from an array of products
  const computeTotal = (prodArray) => {
    return prodArray.reduce((sum, p) => {
      const amt = parseFloat(p.amount) || 0;
      return sum + amt;
    }, 0);
  };

  // ✅ Auto update existing product amounts when overall discount changes
  useEffect(() => {
    if (products.length > 0) {
      const updated = products.map((p) => ({
        ...p,
        amount: calculateAmount(
          p.numOfPacks,
          p.loose,
          p.rate,
          p.discPer,
          p.gstPer,
          overallDiscountPercent
        ),
        marginPer: calculateMargin(
          p.mrp,
          p.rate,
          p.discPer,
          p.gstPer,
          p.numOfPacks,
          p.loose,
          overallDiscountPercent
        ),
      }));
      setProducts(updated);
      // update total immediately
      const total = computeTotal(updated);
      setTotalAmount(Number(total.toFixed(2)));
    } else {
      // if products empty ensure total is 0
      setTotalAmount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallDiscountPercent]);

  // whenever products array changes, update total
  useEffect(() => {
    const total = computeTotal(products);
    setTotalAmount(Number(total.toFixed(2)));
  }, [products, setTotalAmount]);

  const handleNumericChange = (field, value) => {
    // allow only numeric/decimal input
    if (!/^\d*\.?\d*$/.test(value)) return;
    if (value < 0) return;

    // update the form state
    setNewProduct((prev) => ({ ...prev, [field]: value }));

    // compute the temp product based on current newProduct + this change
    const temp = { ...newProduct, [field]: value };

    const marginPer = calculateMargin(
      temp.mrp,
      temp.rate,
      temp.discPer,
      temp.gstPer,
      temp.numOfPacks,
      temp.loose,
      overallDiscountPercent
    );
    const amount = calculateAmount(
      temp.numOfPacks,
      temp.loose,
      temp.rate,
      temp.discPer,
      temp.gstPer,
      overallDiscountPercent
    );

    // update newProduct fields (margin & amount)
    setNewProduct((prev) => ({
      ...prev,
      marginPer,
      amount,
      [field]: value,
    }));

    // compute total as sum of existing products + current newProduct.amount
    const existingTotal = computeTotal(products);
    const total = existingTotal + (parseFloat(amount) || 0);
    setTotalAmount(Number(total.toFixed(2)));
  };

  const handleBatchFieldClick = () => {
    console.log(newProduct);
    if (!newProduct.productBarcode) {
      toast.error("Please select a product first!", {
        position: "top-center",
        style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
      });
      return;
    }
    setShowBatchModal(true);
  };

  const handleBatchSelect = (batch) => {
    // Format expiry date to 'MMM/YYYY' (e.g., Feb/2027)
    let formattedExpiry = "";
    if (batch.expiry) {
      const date = new Date(batch.expiry);
      formattedExpiry = date.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });
    }

    setNewProduct((prev) => ({
      ...prev,
      batchID: batch.batchId || batch.batchNo,
      expiry: formattedExpiry,
      mrp: batch.mrp || "",
      hsNorSAC: batch.hsnCode || "",
      gstPer: parseFloat(batch.gst) || "",
      rate: batch.purcRate || "",
      pack: String(batch.pack) || "",
    }));

    setShowBatchModal(false);
  };

  const addProduct = () => {
    if (!newProduct.productBarcode.trim()) {
      toast.error("Please select a product first!", {
        position: "top-center",
        style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
      });
      return;
    }

    if (!newProduct.batchID.trim()) {
      toast.error("Please select a batch id!", {
        position: "top-center",
        style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
      });
      return;
    }

    if (!newProduct.numOfPacks || parseFloat(newProduct.numOfPacks) <= 0) {
      toast.error("Please enter a valid quantity!", {
        position: "top-center",
        style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
      });
      return;
    }

    const newProd = {
      ...newProduct,
      numOfPacks: parseFloat(newProduct.numOfPacks) || 0,
      rate: parseFloat(newProduct.rate) || 0,
      mrp: parseFloat(newProduct.mrp) || 0,
      discPer: parseFloat(newProduct.discPer) || 0,
      gstPer: parseFloat(newProduct.gstPer) || 0,
    };

    newProd.marginPer = calculateMargin(
      newProd.mrp,
      newProd.rate,
      newProd.discPer,
      newProd.gstPer,
      newProd.numOfPacks,
      newProd.loose,
      overallDiscountPercent
    );

    newProd.amount = calculateAmount(
      newProd.numOfPacks,
      newProd.loose,
      newProd.rate,
      newProd.discPer,
      newProd.gstPer,
      overallDiscountPercent
    );

    if (editIndex !== null) {
      const updated = [...products];
      updated[editIndex] = { ...updated[editIndex], ...newProd };
      setProducts(updated);
      // update total immediately
      const total = computeTotal(updated);
      setTotalAmount(Number(total.toFixed(2)));

      setEditIndex(null);
      toast.success("Product updated successfully!");
    } else {
      const newList = [...products, { ...newProd, id: 0 }];
      setProducts(newList);
      // update total immediately using new list
      const total = computeTotal(newList);
      setTotalAmount(Number(total.toFixed(2)));

      toast.success("Product added!");
    }

    setNewProduct({
      productBarcode: "",
      prodID: "",
      hsNorSAC: "",
      rx: "",
      batchID: "",
      expiry: "",
      pack: "1 x",
      numOfPacks: "",
      loose: "",
      mrp: "",
      rate: "",
      discPer: "",
      gstPer: "",
      marginPer: "",
      amount: 0,
      billID: 0,
      billProdID: 0,
      invoiceID: "",
      isActive: true,
      location: "",
      pharmaID: ""
    });
  };

  const editProduct = (i) => {
    setNewProduct(products[i]);
    setEditIndex(i);
    toast("Edit mode enabled!", {
      icon: "✏️",
      position: "top-center",
      style: { background: "#3b82f6", color: "#fff", fontWeight: 500 },
    });
  };

  const deleteProduct = (i) => {
    const newList = products.filter((_, x) => x !== i);
    setProducts(newList);
    const total = computeTotal(newList);
    setTotalAmount(Number(total.toFixed(2)));
  };

  const percentField = (
    value,
    readOnly = false,
    field = "",
    onChange = () => {}
  ) => (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        inputMode="decimal"
        readOnly={readOnly}
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        className="table-input"
        style={{
          width: "70px",
          textAlign: "right",
          background: readOnly ? "#f9fafb" : "white",
          fontWeight: 600,
          paddingRight: "18px",
        }}
      />
      <span
        style={{
          position: "absolute",
          right: "5px",
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "13px",
          color: "#666",
        }}
      >
        %
      </span>
    </div>
  );

  return (
    <>
      <style>{noArrowsCSS}</style>
      <Toaster />
      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>PRODUCT</th>
              <th>RX</th>
              <th>BATCH</th>
              <th>HSN</th>
              <th>PACK</th>
              <th>EXPIRY</th>
              <th>MRP</th>
              <th>Packs</th>
              <th>LOOSE</th>
              <th>RATE</th>
              <th>DISC</th>
              <th>GST</th>
              <th>MARGIN</th>
              <th>AMT</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {/* Add/Edit Row */}
            <tr className="new-product-row">
              <td></td>
              <td>
                <input
                  type="text"
                  placeholder="Type or Press Space"
                  value={newProduct.productBarcode}
                  onFocus={onShowProductModal}
                  readOnly
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={newProduct.rx}
                  onChange={(e) =>
                    setNewProduct((p) => ({ ...p, rx: e.target.value }))
                  }
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={newProduct.batchID}
                  onClick={handleBatchFieldClick}
                  readOnly
                  className="table-input"
                  placeholder="Click to select"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={newProduct.hsNorSAC}
                  onChange={(e) =>
                    setNewProduct((p) => ({ ...p, hsNorSAC: e.target.value }))
                  }
                  className="table-input"
                />
              </td>

              <td>
                <input
                  type="text"
                  value={newProduct.pack}
                  onChange={(e) =>
                    setNewProduct((p) => ({ ...p, pack: e.target.value }))
                  }
                  className="table-input"
                />
              </td>
              <td>
                <div style={{ position: "relative" }}>
                  <DatePicker
                    format="MMM yyyy"
                    caretAs={BsCalendar2MonthFill}
                    value={
                      newProduct.expiry ? new Date(newProduct.expiry) : null
                    }
                    onChange={(date) => {
                      if (!date) return;
                      const backendFormat = date.toISOString().split("T")[0];
                      setNewProduct((p) => ({ ...p, expiry: backendFormat }));
                    }}
                    style={{
                      width: "120px",
                      fontSize: "12px",
                      border: "1px solid #d1d5db", // matches input default border
                      borderRadius: "6px",
                      padding: "6px 8px",
                      color: "#111827",
                    }}
                    placement="autoVerticalStart"
                    cleanable={false}
                    editable={false}
                    placeholder="Select Month"
                    appearance="subtle"
                    oneTap
                    className="custom-datepicker"
                  />
                </div>

                {/* Inline custom focus style */}
                <style>
                  {`
      .custom-datepicker .rs-input {
        border: none !important;
        font-size: 12px !important;
        color: #111827 !important;
      }

      .custom-datepicker .rs-picker-toggle {
        border: 1px solid #d1d5db !important;
        font-size: 12px !important;
        height: 32px !important;
      }

      .custom-datepicker .rs-picker-toggle:hover,
      .custom-datepicker .rs-picker-toggle:focus,
      .custom-datepicker .rs-picker-toggle:active {
        border-color: #10b981 !important; /* focus color same as other inputs */
        box-shadow: 0 0 0 1px #10b98133 !important;
      }

      .custom-datepicker .rs-picker-toggle-placeholder {
        color: #6b7280 !important; /* gray placeholder */
        font-size: 12px !important;
      }

      .custom-datepicker .rs-picker-toggle-value {
        font-size: 12px !important;
        color: #111827 !important;
        font-weight: 500 !important;
      }

      .custom-datepicker .rs-picker-toggle-caret {
        color: #6b7280 !important;
      }
    `}
                </style>
              </td>
              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.mrp || ""}
                  onChange={(e) => handleNumericChange("mrp", e.target.value)}
                  className="table-input"
                />
              </td>

              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.numOfPacks || ""}
                  onChange={(e) => handleNumericChange("numOfPacks", e.target.value)}
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="number"
                  inputMode="decimal"
                  value={newProduct.loose || 0}
                  onChange={(e) => handleNumericChange("loose", e.target.value)}
                  className="table-input"
                  min={0}
                />
              </td>

              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.rate || ""}
                  onChange={(e) => handleNumericChange("rate", e.target.value)}
                  className="table-input"
                />
              </td>
              <td>
                {percentField(
                  newProduct.discPer,
                  false,
                  "discPer",
                  handleNumericChange
                )}
              </td>
              <td>
                {percentField(
                  newProduct.gstPer,
                  false,
                  "gstPer",
                  handleNumericChange
                )}
              </td>
              <td>{percentField(newProduct.marginPer, true)}</td>
              <td>₹ {newProduct.amount || 0}</td>
              <td>
                <button type="button" className="gradient-button" onClick={addProduct}>
                  {editIndex !== null ? "UPDATE" : "ADD"}
                </button>
              </td>
            </tr>

            {/* Existing Product Rows */}
            {products.map((p, i) => {
               // fetch errors/touched for this row from formik (if provided)
              const rowErrors = formik ? getIn(formik.errors, `products[${i}]`) : null;
              const rowTouched = formik ? getIn(formik.touched, `products[${i}]`) : null;

              const cellError = (field) =>
                rowErrors && rowErrors[field] ? rowErrors[field] : null;

              const isInvalid = (field) => Boolean(cellError(field) && rowTouched && rowTouched[field]);
              return (
              <tr key={p.id}>
                <td>{i + 1}</td>
                <td>{p.productBarcode}</td>
                <td>{p.rx}</td>
                <td>{p.batchID}</td>
                <td>{p.hsNorSAC}</td>
                <td>{p.pack}</td>
                <td>{p.expiry}</td>
                <td>{p.mrp}</td>
                <td>{p.numOfPacks}</td>
                <td>{p.loose}</td>
                <td>{p.rate}</td>
                <td>{p.discPer}%</td>
                <td>{p.gstPer}%</td>
                <td
                  style={{
                    color: parseFloat(p.marginPer) < 0 ? "#ef4444" : "#10b981",
                    fontWeight: 600,
                  }}
                >
                  {p.marginPer}%
                </td>
                <td>₹ {p.amount}</td>
                <td style={{ display: "flex", gap: "10px" }}>
                  <Edit2
                    size={20}
                    style={{
                      marginRight: 10,
                      cursor: "pointer",
                      color: "#464646ff",
                    }}
                    onClick={() => editProduct(i)}
                  />
                  <Trash2
                    size={20}
                    className="delete-icon"
                    onClick={() => deleteProduct(i)}
                  />
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      <BatchSelectModal
        open={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSelect={handleBatchSelect}
        productId={newProduct.prodID}
        productName={newProduct.productBarcode}
      />
    </>
  );
};

export default SaleInvoiceProductTable;
